import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Axios from 'axios';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
// import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Cancel } from '@mui/icons-material';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import { fetchCompanyDocTypes } from 'src/doc-types/actions';
import { fetchDocumentDeliveryFormats } from 'src/shared/redux/documentDeliveryTypes/actions';
import { RootState } from 'src/types';
import { axiosHeaders } from 'src/utils/helpers';
import { useConfig } from 'src/hooks/useConfig';
import { useAuth } from 'src/hooks/useAuth';
import authService from 'src/utils/authService';

import theme from 'src/themeNew';
import styles from './styles';

type NodeSettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
  nodeType: string | null;
  nodeId: string | null;
  label: string | null;
  configuration: Record<string, any>;
  isSaving?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfigurationSave: (nodeId: string, configuration: Record<string, any>) => void;
};

const NodeSettingsDrawer: React.FC<NodeSettingsDrawerProps> = ({
  open,
  onClose,
  nodeType,
  nodeId,
  configuration,
  isSaving = false,
  onConfigurationSave,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { API } = useConfig();

  const companyDocTypes: Record<string, any>[] = useSelector((state: RootState) => state.docTypes.companyDocTypes);
  const deliveryFormats = useSelector((state: RootState) => state.documentDeliveryFormats.documentDeliveryFormats);
  const deliveryFormatsLoading = useSelector((state: RootState) => state.documentDeliveryFormats.documentDeliveryFormatsLoading);

  const [documentInputConfig, setDocumentInputConfig] = useState({
    inputDocumentFormat: '',
    inputSource: '',
  });

  const [documentTypeConfig, setDocumentTypeConfig] = useState({
    selectedDocumentType: '',
  });

  const [users, setUsers] = useState<Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    name: string;
    willReview: boolean;
    loadPercentage: number;
  }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [distributeEqually, setDistributeEqually] = useState(false);

  const [exportConfig, setExportConfig] = useState({
    selectedExportFormats: [] as string[],
    selectedExportDestinations: [] as string[],
  });

  const exportDestinations = [
    { key: 'email', label: 'Email' },
    { key: 'download', label: 'Download' },
    { key: 'ftp', label: 'FTP' },
    { key: 'sftp', label: 'SFTP' },
  ];

  useEffect(() => {
    if (nodeType === 'document_input' && configuration) {
      setDocumentInputConfig({
        inputDocumentFormat: configuration.inputDocumentFormat || '',
        inputSource: configuration.inputSource || '',
      });
    }

    if (nodeType === 'document_type' && configuration) {
      setDocumentTypeConfig({
        selectedDocumentType: configuration.selectedDocumentType || '',
      });
    }

    if (nodeType === 'document_review' && configuration) {
      setDistributeEqually(configuration.distributeEqually || false);
    }

    if (nodeType === 'document_export' && configuration) {
      setExportConfig({
        selectedExportFormats: configuration.selectedExportFormats || [],
        selectedExportDestinations: configuration.selectedExportDestinations || [],
      });
    }
  }, [nodeType, configuration]);

  useEffect(() => {
    if (companyDocTypes.length === 0) {
      dispatch(fetchCompanyDocTypes() as any);
    }
  }, [dispatch, companyDocTypes.length]);

  useEffect(() => {
    if (deliveryFormats.length === 0) {
      dispatch(fetchDocumentDeliveryFormats() as any);
    }
  }, [dispatch, deliveryFormats.length]);

  useEffect(() => {
    if (nodeType === 'document_review' && open) {
      fetchUsers();
    }
  }, [nodeType, open]);

  const fetchUsers = async () => {
    if (!authService.validateToken() || !user?.companyID) {
      return;
    }

    setUsersLoading(true);
    try {
      const response = await Axios.get(
        `${API.getUsersByCompanies}${user.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const usersData = response.data.data.map((userData: any) => ({
          id: userData.id?.toString() || userData.email,
          email: userData.email,
          firstName: userData.firstName || userData.firstname,
          lastName: userData.lastName || userData.lastname,
          name: `${userData.firstName || userData.firstname || ''} ${userData.lastName || userData.lastname || ''}`.trim() || userData.email,
          willReview: false,
          loadPercentage: 0,
        }));

        if (configuration?.users && Array.isArray(configuration.users)) {
          const configUsersMap = new Map(
            configuration.users.map((user: any) => [user.email, user])
          );

          const mergedUsers = usersData.map((userData: any) => {
            const configUser = configUsersMap.get(userData.email);
            if (configUser) {
              return {
                ...userData,
                willReview: configUser.willReview || false,
                loadPercentage: configUser.loadPercentage || 0,
              };
            }
            return userData;
          });

          setUsers(mergedUsers);
        } else {
          setUsers(usersData);
        }
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const totalLoadPercentage = users.reduce((total, user) => total + (user.willReview ? user.loadPercentage : 0), 0);
  const hasError = totalLoadPercentage !== 100;
  const reviewingUsers = users.filter(user => user.willReview);
  const hasValidReviewSetup = reviewingUsers.length > 0 && totalLoadPercentage === 100;

  const handleEditClick = (index: number, currentValue: number) => {
    setEditingIndex(index);
    setEditValue(currentValue.toString());
  };

  const handleSaveEdit = (index: number) => {
    const numericValue = parseInt(editValue, 10);
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      setUsers(prev => prev.map((user, i) =>
        i === index ? { ...user, loadPercentage: numericValue } : user
      ));
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyPress = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter') {
      handleSaveEdit(index);
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleSwitchChange = (index: number, checked: boolean) => {
    setUsers(prev => prev.map((user, i) => {
      if (i === index) {
        const updatedUser = { ...user, willReview: checked };
        if (!checked) {
          updatedUser.loadPercentage = 0;
        }
        return updatedUser;
      }
      return user;
    }));
  };

  const handleDistributeEquallyChange = (checked: boolean) => {
    setDistributeEqually(checked);

    if (checked) {
      const reviewingUsersList = users.filter(user => user.willReview);
      if (reviewingUsersList.length > 0) {
        const equalPercentage = Math.floor(100 / reviewingUsersList.length);
        const remainder = 100 - (equalPercentage * reviewingUsersList.length);

        setUsers(prev => prev.map(user => {
          if (user.willReview) {
            const reviewingIndex = reviewingUsersList.findIndex(u => u.id === user.id);
            return {
              ...user,
              loadPercentage: equalPercentage + (reviewingIndex === 0 ? remainder : 0)
            };
          }
          return user;
        }));
      }
    }
  };

  const handleDocumentInputSave = () => {
    if (nodeId) {
      onConfigurationSave(nodeId, documentInputConfig);
    }
  };

  const handleDocumentTypeSave = () => {
    if (nodeId) {
      onConfigurationSave(nodeId, documentTypeConfig);
    }
  };

  const handleDocumentReviewSave = () => {
    if (nodeId) {
      const configuration = {
        users: users,
        distributeEqually: distributeEqually,
        totalLoadPercentage: totalLoadPercentage
      };
      onConfigurationSave(nodeId, configuration);
    }
  };

  const handleDocumentExportSave = () => {
    if (nodeId) {
      onConfigurationSave(nodeId, exportConfig);
    }
  };

  const handleExportFormatChange = (value: string[]) => {
    setExportConfig(prev => ({
      ...prev,
      selectedExportFormats: value
    }));
  };

  const handleExportDestinationChange = (value: string[]) => {
    setExportConfig(prev => ({
      ...prev,
      selectedExportDestinations: value
    }));
  };

  const isDocumentInputFormValid = () => {
    return documentInputConfig.inputDocumentFormat && documentInputConfig.inputSource;
  };

  const isDocumentTypeFormValid = () => {
    return documentTypeConfig.selectedDocumentType;
  };

  const isDocumentExportFormValid = () => {
    return exportConfig.selectedExportFormats.length > 0 && exportConfig.selectedExportDestinations.length > 0;
  };

  const renderDrawerContent = () => {
    if (!nodeType) return null;

    if (nodeType === 'document_input') {
      return (
        <Box sx={styles.container}>
          <Box sx={styles.modalHeader}>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.headerTitle}>
                {t('WORKFLOW_NODE_SETTINGS_DOCUMENT_INPUT')}
              </Typography>
              <IconButton sx={styles.closeBtn} onClick={onClose}>
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={styles.modalContent}>
            <Box sx={styles.form}>
              <Box sx={styles.fieldContainer}>
                <FormControl fullWidth sx={styles.select}>
                  <InputLabel>{t('WORKFLOW_DOCUMENT_INPUT_FORMAT_LABEL')}</InputLabel>
                  <Select
                    label={t('WORKFLOW_DOCUMENT_INPUT_FORMAT_LABEL')}
                    value={documentInputConfig.inputDocumentFormat}
                    onChange={(e) => setDocumentInputConfig(prev => ({ ...prev, inputDocumentFormat: e.target.value }))}
                  >
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="jpg">JPG</MenuItem>
                    <MenuItem value="png">PNG</MenuItem>
                    <MenuItem value="tiff">TIFF</MenuItem>
                    <MenuItem value="bmp">BMP</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={styles.fieldContainer}>
                <FormControl fullWidth sx={styles.select}>
                  <InputLabel>{t('WORKFLOW_DOCUMENT_INPUT_SOURCE_LABEL')}</InputLabel>
                  <Select
                    label={t('WORKFLOW_DOCUMENT_INPUT_SOURCE_LABEL')}
                    value={documentInputConfig.inputSource}
                    onChange={(e) => setDocumentInputConfig(prev => ({ ...prev, inputSource: e.target.value }))}
                  >
                    <MenuItem value="upload">{t('WORKFLOW_DOCUMENT_INPUT_SOURCE_UPLOAD')}</MenuItem>
                    <MenuItem value="email">{t('WORKFLOW_DOCUMENT_INPUT_SOURCE_EMAIL')}</MenuItem>
                    <MenuItem value="ftp">{t('WORKFLOW_DOCUMENT_INPUT_SOURCE_FTP')}</MenuItem>
                    <MenuItem value="api">{t('WORKFLOW_DOCUMENT_INPUT_SOURCE_API')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.modalFooter}>
            <Box display="flex" justifyContent="flex-end" gap="16px">
              <CancelBtn
                title={t('WORKFLOW_BUTTON_CANCEL')}
                onClick={onClose}
              />
              <SubmitBtn
                title={t('WORKFLOW_BUTTON_SAVE')}
                disabled={!isDocumentInputFormValid() || isSaving}
                onClick={handleDocumentInputSave}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (nodeType === 'digitize') {
      return (
        <Box sx={styles.container}>
          <Box sx={styles.modalHeader}>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.headerTitle}>
                {t('WORKFLOW_NODE_SETTINGS_DIGITIZE')}
              </Typography>
              <IconButton sx={styles.closeBtn} onClick={onClose}>
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={styles.modalContent}>
            <Typography sx={styles.description}>
              {t('WORKFLOW_DIGITIZE_DESCRIPTION')}
            </Typography>
          </Box>
          <Box sx={styles.modalFooter}>
            <Box display="flex" justifyContent="flex-end" gap="16px">
              <CancelBtn
                title={t('WORKFLOW_BUTTON_CLOSE')}
                onClick={onClose}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (nodeType === 'document_type') {
      return (
        <Box sx={styles.container}>
          <Box sx={styles.modalHeader}>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.headerTitle}>
                {t('WORKFLOW_NODE_SETTINGS_DOCUMENT_TYPE')}
              </Typography>
              <IconButton sx={styles.closeBtn} onClick={onClose}>
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={styles.modalContent}>
            <Box sx={styles.form}>
              <Box sx={styles.fieldContainer}>
                <FormControl fullWidth sx={styles.select}>
                  <InputLabel>{t('WORKFLOW_DOCUMENT_TYPE_LABEL')}</InputLabel>
                  <Select
                    label={t('WORKFLOW_DOCUMENT_TYPE_LABEL')}
                    value={documentTypeConfig.selectedDocumentType}
                    onChange={(e) => setDocumentTypeConfig(prev => ({ ...prev, selectedDocumentType: e.target.value }))}
                  >
                    {companyDocTypes.map((docType) => (
                      <MenuItem key={docType.documentType} value={docType.documentType}>
                        {docType.title || docType.documentType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.modalFooter}>
            <Box display="flex" justifyContent="flex-end" gap="16px">
              <CancelBtn
                title={t('WORKFLOW_BUTTON_CANCEL')}
                onClick={onClose}
              />
              <SubmitBtn
                title={t('WORKFLOW_BUTTON_SAVE')}
                disabled={!isDocumentTypeFormValid() || isSaving}
                onClick={handleDocumentTypeSave}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (nodeType === 'document_review') {
      return (
        <Box sx={{ ...styles.container, width: '500px' }}>
          <Box sx={styles.modalHeader}>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.headerTitle}>
                {t('WORKFLOW_NODE_SETTINGS_REVIEW')}
              </Typography>
              <IconButton sx={styles.closeBtn} onClick={onClose}>
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={styles.modalContent}>
            <TableContainer>
              <Table sx={styles.reviewTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('WORKFLOW_REVIEW_TABLE_USER')}</TableCell>
                    <TableCell>{t('WORKFLOW_REVIEW_TABLE_WILL_REVIEW')}</TableCell>
                    <TableCell>{t('WORKFLOW_REVIEW_TABLE_LOAD_PERCENTAGE')}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography>{t('WORKFLOW_REVIEW_LOADING_USERS')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography>{t('WORKFLOW_REVIEW_NO_USERS')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, idx) => (
                      <TableRow key={user.id || user.email || idx}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <Switch
                            checked={user.willReview}
                            onChange={(e) => handleSwitchChange(idx, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          {editingIndex === idx ? (
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => handleKeyPress(e, idx)}
                              inputProps={{
                                min: 0,
                                max: 100,
                              }}
                              type="number"
                              sx={{
                                ...styles.editInput,
                                width: '80px',
                              }}
                              autoFocus
                              disabled={!user.willReview}
                            />
                          ) : (
                            `${user.loadPercentage}%`
                          )}
                        </TableCell>
                        <TableCell>
                          {editingIndex === idx ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleSaveEdit(idx)}
                                sx={{
                                  ...styles.actionButton,
                                  color: 'success.main'
                                }}
                                disabled={!user.willReview}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{
                                  ...styles.actionButton,
                                  color: 'error.main'
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(idx, user.loadPercentage)}
                                sx={styles.actionButton}
                                disabled={!user.willReview}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {/* <IconButton
                                size="small"
                                sx={styles.actionButton}
                                disabled
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton> */}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {hasError && reviewingUsers.length > 0 && (
            <Box sx={styles.errorCard}>
              <Cancel
                sx={styles.errorIcon}
              />
              <Typography sx={styles.errorText}>
                {t('WORKFLOW_REVIEW_ERROR_LOAD_PERCENTAGE')}
              </Typography>
            </Box>
          )}
          {reviewingUsers.length === 0 && (
            <Box sx={styles.errorCard}>
              <Cancel
                sx={styles.errorIcon}
              />
              <Typography sx={styles.errorText}>
                {t('WORKFLOW_REVIEW_ERROR_NO_REVIEWERS')}
              </Typography>
            </Box>
          )}
          <Box sx={styles.distributeSection}>
            <Box sx={styles.distributeSectionRow}>
              <Box sx={styles.distributeContainer}>
                <Typography sx={{ fontSize: '13px', color: theme.palette.text.primary }}>
                  {t('WORKFLOW_REVIEW_DISTRIBUTE_EQUALLY')}
                </Typography>
                <Switch
                  checked={distributeEqually}
                  onChange={(e) => handleDistributeEquallyChange(e.target.checked)}
                  disabled={reviewingUsers.length === 0}
                />
              </Box>
              <Box sx={styles.statusContainer}>
                <Typography color="success.main" sx={styles.statusText}>
                  {t('WORKFLOW_REVIEW_LOAD')}: {totalLoadPercentage}%
                </Typography>
                <Box sx={styles.verticalSeparator} />
                <Typography color="error.main" sx={styles.statusText}>
                  {t('WORKFLOW_REVIEW_PENDING')}: {100 - totalLoadPercentage}%
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.modalFooter}>
            <Box display="flex" justifyContent="flex-end" gap="16px">
              <CancelBtn
                title={t('WORKFLOW_BUTTON_CANCEL')}
                onClick={onClose}
              />
              <SubmitBtn
                title={t('WORKFLOW_BUTTON_SAVE')}
                disabled={!hasValidReviewSetup || isSaving}
                onClick={handleDocumentReviewSave}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    if (nodeType === 'document_export') {
      return (
        <Box sx={styles.container}>
          <Box sx={styles.modalHeader}>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.headerTitle}>
                {t('WORKFLOW_NODE_SETTINGS_EXPORT')}
              </Typography>
              <IconButton sx={styles.closeBtn} onClick={onClose}>
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={styles.modalContent}>
            <Box sx={styles.form}>
              <Box sx={styles.fieldContainer}>
                <FormControl fullWidth sx={styles.select}>
                  <InputLabel>{t('WORKFLOW_EXPORT_FORMATS_LABEL')}</InputLabel>
                  <Select
                    label={t('WORKFLOW_EXPORT_FORMATS_LABEL')}
                    multiple
                    value={exportConfig.selectedExportFormats}
                    onChange={(e) => handleExportFormatChange(e.target.value as string[])}
                    disabled={deliveryFormatsLoading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const format = deliveryFormats.find(f => f.key === value);
                          return (
                            <Chip
                              key={value}
                              label={format?.label || value}
                              size="small"
                              sx={{ margin: '2px' }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {deliveryFormats.map((format) => (
                      <MenuItem key={format.key} value={format.key}>
                        {format.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={styles.fieldContainer}>
                <FormControl fullWidth sx={styles.select}>
                  <InputLabel>{t('WORKFLOW_EXPORT_DESTINATIONS_LABEL')}</InputLabel>
                  <Select
                    label={t('WORKFLOW_EXPORT_DESTINATIONS_LABEL')}
                    multiple
                    value={exportConfig.selectedExportDestinations}
                    onChange={(e) => handleExportDestinationChange(e.target.value as string[])}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const destination = exportDestinations.find(d => d.key === value);
                          return (
                            <Chip
                              key={value}
                              label={destination?.label || value}
                              size="small"
                              sx={{ margin: '2px' }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {exportDestinations.map((destination) => (
                      <MenuItem key={destination.key} value={destination.key}>
                        {destination.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.modalFooter}>
            <Box display="flex" justifyContent="flex-end" gap="16px">
              <CancelBtn
                title={t('WORKFLOW_BUTTON_CANCEL')}
                onClick={onClose}
              />
              <SubmitBtn
                title={t('WORKFLOW_BUTTON_SAVE')}
                disabled={!isDocumentExportFormValid() || isSaving}
                onClick={handleDocumentExportSave}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      {renderDrawerContent()}
    </Drawer>
  );
};

export default NodeSettingsDrawer;
